// Settings: user preferences and project management, reached from the third
// bottom-nav tab. Scoped to a daemon (#s=<id>) so the project switcher and the
// rest of the nav stay anchored, but the daemon-management section spans every
// paired project. No live connection here — status dots use the lightweight
// reachability probe, the same as the switcher.
import { el, clear } from "../dom.js";
import { daemonStatus } from "../relay.js";
import { listDaemons, removeDaemon } from "../store.js";
import { bottomNav } from "../nav.js";
import { projectTitle } from "../topbar.js";
import { openScanner } from "./scanner.js";
import type { DaemonEntry } from "../store.js";

export interface SettingsNav {
  onSwitch: () => void;
  onFeed: () => void;
  onBoard: () => void;
  onNotifications: () => void;
  onAgents: () => void;
  // Re-route after the current daemon is forgotten (it no longer exists, so the
  // app must land somewhere valid — another project, or the welcome screen).
  onReroute: () => void;
}

export function renderSettings(root: HTMLElement, entry: DaemonEntry, nav: SettingsNav): void {
  clear(root);

  const dot = el("span", { class: "status-dot" });
  void daemonStatus(entry).then((s) => {
    dot.className = `status-dot ${s.online ? "online" : "offline"}`;
  });

  const daemonList = el("div", { class: "settings-list" });

  const renderDaemons = () => {
    daemonList.replaceChildren();
    for (const d of listDaemons()) {
      const rowDot = el("span", { class: "status-dot" });
      const sub = el("span", { class: "sub" }, ["checking…"]);
      void daemonStatus(d).then((s) => {
        rowDot.className = `status-dot ${s.online ? "online" : "offline"}`;
        sub.textContent = s.online ? "online" : "offline";
      });
      daemonList.append(
        el("div", { class: "settings-row daemon-row" }, [
          el("div", { style: "flex:1;min-width:0" }, [
            el("div", { class: "name" }, [rowDot, " ", d.name]),
            sub,
          ]),
          el("button", { class: "danger", onclick: () => {
            if (!confirm(`Forget “${d.name}”? You'll need a new QR to re-pair.`)) return;
            const wasCurrent = d.daemonId === entry.daemonId;
            removeDaemon(d.daemonId);
            if (wasCurrent) { nav.onReroute(); return; }
            renderDaemons();
          } }, ["Forget"]),
        ]),
      );
    }
    daemonList.append(
      el("button", { class: "settings-pair", onclick: () => openScanner((hash) => { location.hash = hash; }) }, ["＋ Pair new device"]),
    );
  };
  renderDaemons();

  root.append(
    el("header", { class: "topbar" }, [
      projectTitle(dot, entry.name, "Settings", nav.onSwitch),
    ]),
    el("div", { class: "settings-section" }, [
      el("div", { class: "settings-label" }, ["Notifications"]),
      el("button", { class: "settings-link", onclick: nav.onNotifications }, [
        el("span", {}, ["Notifications & push"]),
        el("span", { class: "settings-chevron" }, ["›"]),
      ]),
      el("button", { class: "settings-link", onclick: nav.onAgents }, [
        el("span", {}, ["Agents"]),
        el("span", { class: "settings-chevron" }, ["›"]),
      ]),
    ]),
    el("div", { class: "settings-section" }, [
      el("div", { class: "settings-label" }, ["Projects"]),
      daemonList,
    ]),
    bottomNav({ active: "settings", onFeed: nav.onFeed, onBoard: nav.onBoard, onSettings: () => {} }),
  );
}
